/**
 * 官网收款（PAY-1）· 样稿 designs/mockups/site-payments/官网收款-样稿.html
 *
 * 手机站客人扫码付全款、传截图 → OP 初筛（通过即确认订单、发凭证）→ 财务隔段查账（对微信 / 支付宝商户流水）。
 * 订单中心和财务进来是同一页（/admin/site-payments?stage=screen|audit），数据一样。
 * 业务全在后端（services/site_payments.py）；这里只展示接口给的数并调用动作端点（铁律十三）。
 * 接口：GET /api/v1/site-payments?stage= · POST …/{id}:screen-pass|screen-reject|mark-missing|recheck · POST …:mark-received
 *      GET|PUT /api/v1/finance/fx-rate
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { AuthedImage } from "@/shared/ui/AuthedImage";
import {
  METHOD_CN, cny, fmtMD, fmtTime, sitePaymentsApi, usd, waitedText,
  type PaymentStage, type SitePaymentRow,
} from "@/services/sitePayments";
import { Drawer, Modal, errText } from "./ui";
import { FxDialogs } from "./FxDialogs";

const TABS: { key: PaymentStage; label: string; tone: string }[] = [
  { key: "screen", label: "待初筛", tone: "bg-blue-600" },
  { key: "audit", label: "待查账", tone: "bg-slate-400" },
  { key: "exception", label: "异常", tone: "bg-rose-600" },
  { key: "done", label: "已完成", tone: "bg-slate-400" },
];
const STAGE_HINT: Record<PaymentStage, string> = {
  screen: "按提交时间排，最早的在上面 · 超过 2 小时没人初筛 → 铃铛提醒 OP 和财务",
  audit: "财务拿微信 / 支付宝商户后台流水一批批对 · 出发前 3 天还没查账 → 铃铛提醒财务和老板",
  exception: "财务标了「没到账」的单 · 已铃铛通知 OP 和老板 · 订单在订单中心、调度中心标红 · 出发前必须处理",
  done: "初筛通过 + 财务已到账的单，只读留档；也能看退回的单和原因",
};

export function SitePaymentsPage() {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("stage");
  const stage: PaymentStage = raw === "audit" || raw === "exception" || raw === "done" ? raw : "screen";
  const q = useQuery({ queryKey: ["site-payments", "list", stage], queryFn: () => sitePaymentsApi.list(stage), refetchInterval: 60_000 });
  const [fxOpen, setFxOpen] = useState<"edit" | "history" | null>(null);
  const canFx = useCan("finance.fx_rate.manage");

  if (q.isError) return <ErrorState message="官网收款加载失败" onRetry={() => q.refetch()} />;
  if (!q.data) return <LoadingState />;
  const { counts, items, fx } = q.data;

  return (
    <PageShell
      title="官网收款"
      subtitle="手机站客人扫码付全款、传截图 → OP 初筛（通过即确认订单、发凭证）→ 财务隔段查账（对微信 / 支付宝商户流水）。订单中心和财务进来是同一页，数据一样。"
    >
      <div data-testid="fx-bar" className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] text-slate-600">
        {fx.current ? (
          <span>当前汇率 <b className="tabular-nums text-slate-900">1 美元 = {fx.current.rate} 人民币</b> · {fx.current.set_by ?? "—"} {fmtTime(fx.current.effective_at)} 设定</span>
        ) : (
          <span className="font-semibold text-rose-600">还没设置汇率——手机站现在不能下单，请先设置</span>
        )}
        {canFx && <button type="button" onClick={() => setFxOpen("edit")} className="rounded-lg border border-slate-300 px-3 py-1 text-[12px] font-medium hover:bg-slate-50">改汇率</button>}
        <button type="button" onClick={() => setFxOpen("history")} className="rounded-lg border border-slate-300 px-3 py-1 text-[12px] font-medium hover:bg-slate-50">历史</button>
      </div>

      <div role="tablist" className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const n = counts[t.key] ?? 0;
          const active = t.key === stage;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setSp({ stage: t.key }, { replace: true })}
              className={`border-b-2 px-5 py-2 text-[14px] font-medium ${active ? "border-blue-600 font-semibold text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              {t.label}
              {t.key !== "done" && n > 0 && <span className={`ml-1.5 inline-block min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-bold text-white ${t.tone}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="mb-3 text-[12px] text-slate-400">{STAGE_HINT[stage]}</p>
        {stage === "screen" && <ScreenTable rows={items} />}
        {stage === "audit" && <AuditTable rows={items} />}
        {stage === "exception" && <ExceptionTable rows={items} />}
        {stage === "done" && <DoneTable rows={items} />}
      </section>

      {fxOpen && <FxDialogs mode={fxOpen} fx={fx} onClose={() => setFxOpen(null)} />}
    </PageShell>
  );
}

// ───────────────────────── 表格 ─────────────────────────

const TH = "px-3 py-2 text-left text-[12px] font-medium text-slate-400 border-b border-slate-200";
const TD = "px-3 py-2.5 align-middle text-[13px] border-b border-slate-100";

function Empty({ text }: { text: string }) {
  return <div data-testid="empty" className="py-10 text-center text-[13px] text-slate-400">{text}</div>;
}
function OrderNo({ no }: { no: string }) {
  return <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px]">{no}</span>;
}
function Amount({ r }: { r: SitePaymentRow }) {
  return <div className="tabular-nums">{cny(r.cny_total_fen)}<div className="text-[11.5px] text-slate-400">{usd(r.usd_total_cents)}</div></div>;
}
function FullPill() {
  return <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-600"><span className="h-1.5 w-1.5 rounded-full bg-current" />那天已满</span>;
}

function ScreenTable({ rows }: { rows: SitePaymentRow[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const canScreen = useCan("site_payments.screen");
  if (rows.length === 0) return <Empty text="没有待初筛的付款 🎉" />;
  return (
    <>
      <table className="w-full border-collapse">
        <thead><tr>{["提交时间", "订单号", "线路 · 出发日", "客人", "应付", "方式", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid="row-screen" className={r.overdue ? "bg-orange-50" : ""}>
              <td className={`${TD} tabular-nums`}>{fmtTime(r.submitted_at)}{r.overdue && <div className="text-[11.5px] font-semibold text-amber-600">{waitedText(r.waited_minutes ?? 0)}</div>}</td>
              <td className={TD}><OrderNo no={r.order_no} /></td>
              <td className={TD}>{r.product} · {fmtMD(r.travel_date)}{r.needs_reschedule_or_refund && <FullPill />}</td>
              <td className={TD}>{r.customer} {r.phone}</td>
              <td className={TD}><Amount r={r} /></td>
              <td className={TD}>{METHOD_CN[r.payment_method ?? ""] ?? "—"}</td>
              <td className={TD}>{canScreen && <button type="button" onClick={() => setOpenId(r.id)} className="rounded-lg bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700">初筛</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {openId && <ScreenDrawer id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function AuditTable({ rows }: { rows: SitePaymentRow[] }) {
  const qc = useQueryClient();
  const canAudit = useCan("site_payments.audit");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [missing, setMissing] = useState<SitePaymentRow | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const total = rows.filter((r) => picked.has(r.id)).reduce((s, r) => s + (r.cny_total_fen ?? 0), 0);
  const recv = useMutation({
    mutationFn: () => sitePaymentsApi.markReceived([...picked]),
    onSuccess: (r) => { setPicked(new Set()); setMsg(`已标 ${r.done} 张到账，合计 ${cny(r.total_cny_fen)}${r.skipped.length ? `；${r.skipped.length} 张不在待查账，已跳过` : ""}`); qc.invalidateQueries({ queryKey: ["site-payments"] }); },
    onError: (e) => setMsg(errText(e)),
  });
  if (rows.length === 0) return <Empty text="没有待查账的付款" />;
  return (
    <>
      {canAudit && (
        <div className="mb-3 flex items-center gap-3">
          <button type="button" disabled={picked.size === 0 || recv.isPending} onClick={() => recv.mutate()} className="rounded-lg bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-40">
            勾选的 {picked.size} 张标「已到账」
          </button>
          <span className="text-[12px] text-slate-500">已选 {picked.size} 张 · 合计 {cny(total)}</span>
          {msg && <span data-testid="audit-msg" className="text-[12px] text-emerald-700">{msg}</span>}
        </div>
      )}
      <table className="w-full border-collapse">
        <thead><tr>{[canAudit ? "" : null, "付款时间", "订单号", "金额", "方式", "初筛人", "出发日", ""].filter((h) => h !== null).map((h, i) => <th key={i} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid="row-audit" className={r.urgent ? "bg-orange-50" : ""}>
              {canAudit && <td className={TD}><input type="checkbox" aria-label={`选择 ${r.order_no}`} checked={picked.has(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4" /></td>}
              <td className={`${TD} tabular-nums`}>{fmtTime(r.screened_at ?? r.submitted_at)}</td>
              <td className={TD}><OrderNo no={r.order_no} /></td>
              <td className={`${TD} tabular-nums`}>{cny(r.cny_total_fen)}</td>
              <td className={TD}>{METHOD_CN[r.payment_method ?? ""] ?? "—"}</td>
              <td className={TD}>{r.screened_by ?? "—"}</td>
              <td className={`${TD} tabular-nums`}>{fmtMD(r.travel_date)}{r.urgent && r.days_left != null && <span data-testid="urgent" className="ml-1.5 rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-600">{r.days_left > 0 ? `还剩 ${r.days_left} 天` : r.days_left === 0 ? "今天出发" : "已过出发日"}</span>}</td>
              <td className={TD}>{canAudit && <button type="button" onClick={() => setMissing(r)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] hover:bg-slate-50">没到账</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {missing && <MissingDialog row={missing} onClose={() => setMissing(null)} />}
    </>
  );
}

function ExceptionTable({ rows }: { rows: SitePaymentRow[] }) {
  const qc = useQueryClient();
  const canAudit = useCan("site_payments.audit");
  const [err, setErr] = useState<string | null>(null);
  const recheck = useMutation({
    mutationFn: (id: string) => sitePaymentsApi.recheck(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["site-payments"] }),
    onError: (e) => setErr(errText(e)),
  });
  if (rows.length === 0) return <Empty text="没有异常的付款" />;
  return (
    <>
      {err && <div className="mb-2 text-[12px] text-rose-600">{err}</div>}
      <table className="w-full border-collapse">
        <thead><tr>{["订单号", "线路 · 出发日", "金额", "财务说明", "处理"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid="row-exception">
              <td className={TD}><OrderNo no={r.order_no} /></td>
              <td className={TD}>{r.product} · {fmtMD(r.travel_date)}{r.urgent && r.days_left != null && <span className="ml-1.5 rounded-full bg-rose-50 px-2 py-0.5 text-[11.5px] font-medium text-rose-600">还剩 {Math.max(r.days_left, 0)} 天</span>}</td>
              <td className={`${TD} tabular-nums`}>{cny(r.cny_total_fen)}</td>
              <td className={TD}>{r.note}</td>
              <td className={TD}>
                <div className="flex gap-2">
                  {canAudit && <button type="button" disabled={recheck.isPending} onClick={() => recheck.mutate(r.id)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] hover:bg-slate-50">已补款，重新查账</button>}
                  <Link to={`/orders/${r.id}`} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] hover:bg-slate-50">取消订单</Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DoneTable({ rows }: { rows: SitePaymentRow[] }) {
  if (rows.length === 0) return <Empty text="还没有已完成的付款" />;
  return (
    <table className="w-full border-collapse">
      <thead><tr>{["订单号", "线路 · 出发日", "金额", "方式", "结果", "处理人"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} data-testid="row-done">
            <td className={TD}><OrderNo no={r.order_no} /></td>
            <td className={TD}>{r.product} · {fmtMD(r.travel_date)}</td>
            <td className={`${TD} tabular-nums`}>{cny(r.cny_total_fen)}</td>
            <td className={TD}>{METHOD_CN[r.payment_method ?? ""] ?? "—"}</td>
            <td className={TD}>
              {r.stage === "rejected"
                ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[12px] font-medium text-rose-600">已退回 · {r.reject_reason}</span>
                : <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-medium text-emerald-700">已到账 {fmtTime(r.audited_at)}</span>}
            </td>
            <td className={TD}>{r.stage === "rejected" ? r.screened_by : r.audited_by}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ───────────────────────── 初筛抽屉 / 退回 / 没到账 ─────────────────────────

function ScreenDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ["site-payments", "detail", id], queryFn: () => sitePaymentsApi.detail(id) });
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [checked, setChecked] = useState({ amount: false, ref: false });
  const done = () => { qc.invalidateQueries({ queryKey: ["site-payments"] }); onClose(); };
  const pass = useMutation({ mutationFn: () => sitePaymentsApi.screenPass(id), onSuccess: done, onError: (e) => setErr(errText(e)) });
  const r = d.data;
  return (
    <Drawer title={<>初筛 · <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px]">{r?.order_no ?? "…"}</span></>} onClose={onClose}>
      {d.isError && <ErrorState message="详情加载失败" onRetry={() => d.refetch()} />}
      {!r && !d.isError && <LoadingState />}
      {r && (
        <div className="flex flex-col gap-3.5">
          <div className="flex h-[300px] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
            {r.proof ? <AuthedImage src={r.proof.url} alt="付款截图" className="max-h-full max-w-full object-contain" /> : <span className="text-slate-400">没有付款截图</span>}
          </div>
          <div className="text-[12.5px] text-slate-600">
            {r.customer} · {r.phone}{r.wechat ? ` · 微信 ${r.wechat}` : ""} · {r.rooms_note} · 出发 {fmtMD(r.travel_date)} · {METHOD_CN[r.payment_method ?? ""] ?? ""}
          </div>
          <div className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 text-[13px]">
            <b>对两样</b>
            <label className="flex items-center gap-2"><input type="checkbox" checked={checked.amount} onChange={(e) => setChecked({ ...checked, amount: e.target.checked })} />截图金额 = 应付 <b className="tabular-nums">{cny(r.cny_total_fen)}</b>（{usd(r.usd_total_cents)} × {r.fx_rate ?? "?"}）</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={checked.ref} onChange={(e) => setChecked({ ...checked, ref: e.target.checked })} />备注里有付款码 <span className="rounded bg-slate-100 px-1.5 font-mono text-[12px]">{r.payment_ref ?? "—"}</span></label>
          </div>
          {r.needs_reschedule_or_refund && (
            <div data-testid="full-note" className="rounded-xl bg-rose-50 px-3 py-2.5 text-[13px] text-rose-600">
              ⚠️ 这张单那天已满：钱照收、照样初筛；通过后<b>不发凭证</b>，转客服联系客人改期或退款（调度问题清单里也有这张单）
            </div>
          )}
          {err && <div data-testid="screen-error" className="text-[12.5px] text-rose-600">{err}</div>}
          <div className="flex gap-2">
            <button type="button" disabled={pass.isPending} onClick={() => pass.mutate()} className="flex-1 rounded-lg bg-blue-600 py-2 text-[13px] font-semibold text-white disabled:opacity-50">{pass.isPending ? "处理中…" : "初筛通过，确认订单"}</button>
            <button type="button" onClick={() => setRejecting(true)} className="flex-1 rounded-lg border border-slate-300 py-2 text-[13px] font-medium hover:bg-slate-50">退回</button>
          </div>
          <p className="text-[11.5px] text-slate-400">通过 → 订单确认、发凭证和确认通知、进「待查账」。退回 → 必须写原因、座位放回、客人「我的订单」显示「付款未通过，请联系客服」、铃铛通知客服。都写操作日志。</p>
          {rejecting && <RejectDialog id={id} reasons={r.reject_reasons} onClose={() => setRejecting(false)} onDone={done} />}
        </div>
      )}
    </Drawer>
  );
}

function RejectDialog({ id, reasons, onClose, onDone }: { id: string; reasons: { code: string; label: string }[]; onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState(reasons[0]?.code ?? "amount");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({ mutationFn: () => sitePaymentsApi.screenReject(id, code, note.trim() || undefined), onSuccess: onDone, onError: (e) => setErr(errText(e)) });
  const needNote = code === "other";
  return (
    <Modal title="退回付款" onClose={onClose}>
      <div className="text-[12.5px] text-slate-500">原因（必填，客服联系客人时会看到）</div>
      <div className="flex flex-wrap gap-1.5">
        {reasons.map((x) => (
          <button key={x.code} type="button" onClick={() => setCode(x.code)} className={`rounded-full border px-3 py-1 text-[12.5px] ${code === x.code ? "border-blue-600 bg-blue-600 font-semibold text-white" : "border-slate-200 bg-white text-slate-600"}`}>{x.label}</button>
        ))}
      </div>
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={needNote ? "选「其它」必须写说明" : "补充说明"} className="w-full rounded-lg border border-slate-300 px-2.5 py-2 text-[13px]" />
      {err && <div className="text-[12.5px] text-rose-600">{err}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px]">取消</button>
        <button type="button" disabled={m.isPending || (needNote && !note.trim())} onClick={() => m.mutate()} className="rounded-lg bg-rose-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-40">确认退回</button>
      </div>
    </Modal>
  );
}

function MissingDialog({ row, onClose }: { row: SitePaymentRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => sitePaymentsApi.markMissing(row.id, note.trim()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["site-payments"] }); onClose(); },
    onError: (e) => setErr(errText(e)),
  });
  return (
    <Modal title="标为「没到账」" onClose={onClose}>
      <div className="text-[12.5px] text-slate-500"><span className="font-mono">{row.order_no}</span> 这张单会进「异常」，马上铃铛通知 OP 和老板，订单标红，出发前必须补款或取消。</div>
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="说明（必填）：比如流水里没有这笔" className="w-full rounded-lg border border-slate-300 px-2.5 py-2 text-[13px]" />
      {err && <div className="text-[12.5px] text-rose-600">{err}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px]">取消</button>
        <button type="button" disabled={m.isPending || !note.trim()} onClick={() => m.mutate()} className="rounded-lg bg-rose-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-40">确认没到账</button>
      </div>
    </Modal>
  );
}

